/**
 * CacheService — in-memory cache with TTL, inflight dedupe, scoped invalidation.
 */

const cache = new Map();
const inflight = new Map();
let cacheEpoch = 0;
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const NEGATIVE_TTL = 60 * 1000;

export class CacheService {
    static get(key) {
        const entry = cache.get(key);
        if (!entry) return null;

        if (Date.now() > entry.expiresAt) {
            cache.delete(key);
            return null;
        }

        return entry.value;
    }

    /** Return cached value even if expired (for SWR / stale-if-error). */
    static getStale(key) {
        const entry = cache.get(key);
        if (!entry) return null;
        return {
            value: entry.value,
            expired: Date.now() > entry.expiresAt,
            expiresAt: entry.expiresAt,
        };
    }

    static set(key, value, ttl = DEFAULT_TTL) {
        const resolvedTtl = (Array.isArray(value) && value.length === 0)
            ? Math.min(ttl, NEGATIVE_TTL)
            : ttl;
        cache.set(key, {
            value,
            expiresAt: Date.now() + resolvedTtl,
        });
    }

    static invalidate(key) {
        cacheEpoch += 1;
        if (key) {
            cache.delete(key);
            inflight.delete(key);
        } else {
            cache.clear();
            inflight.clear();
        }
    }

    static invalidatePattern(pattern) {
        cacheEpoch += 1;
        const regex = new RegExp(pattern);
        for (const key of cache.keys()) {
            if (regex.test(key)) {
                cache.delete(key);
            }
        }
        for (const key of inflight.keys()) {
            if (regex.test(key)) {
                inflight.delete(key);
            }
        }
    }

    static getOrSet(key, fn, ttl = DEFAULT_TTL) {
        const cached = this.get(key);
        if (cached !== null) return Promise.resolve(cached);

        if (inflight.has(key)) return inflight.get(key);

        const stale = this.getStale(key);
        const epoch = cacheEpoch;
        const promise = Promise.resolve()
            .then(fn)
            .then(value => {
                if (epoch === cacheEpoch) {
                    this.set(key, value, ttl);
                }
                inflight.delete(key);
                return value;
            })
            .catch(err => {
                inflight.delete(key);
                // stale-if-error: keep serving previous value when refresh fails
                if (stale && stale.value !== null && stale.value !== undefined) {
                    return stale.value;
                }
                throw err;
            });

        inflight.set(key, promise);
        return promise;
    }

    static has(key) {
        const entry = cache.get(key);
        if (!entry) return false;

        if (Date.now() > entry.expiresAt) {
            cache.delete(key);
            return false;
        }

        return true;
    }

    static size() {
        return cache.size;
    }

    static clear() {
        cacheEpoch += 1;
        cache.clear();
        inflight.clear();
    }
}

function invalidateScoped(patterns) {
    for (const pattern of patterns) {
        CacheService.invalidatePattern(pattern);
    }
}

// Scoped invalidation — avoid nuclear clear on every mutation
if (typeof window !== 'undefined') {
    const SCOPED = {
        dealSaved: [/^deals/, /^schedules/, /^contacts:enriched/, /^pages:\/api\/v1\/commissions/],
        dealDeleted: [/^deals/, /^schedules/, /^contacts:enriched/],
        clientSaved: [/^clients/, /^contacts/],
        clientDeleted: [/^clients/, /^contacts/],
        contactSaved: [/^contacts/, /^contact:/],
        contactDeleted: [/^contacts/, /^contact:/],
        userSaved: [/^users/],
        userDeleted: [/^users/],
        pipelineCreated: [/^pipelines/],
        pipelineDeleted: [/^pipelines/],
        dealActivitiesChanged: [/^deals/, /^activities/],
    };

    Object.entries(SCOPED).forEach(([evt, patterns]) => {
        window.addEventListener(evt, () => invalidateScoped(patterns));
    });

    // Full clear only for auth / bulk import
    window.addEventListener('dataImported', () => CacheService.clear());
    window.addEventListener('authChanged', () => CacheService.clear());
}
